import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/ui/StateBoundaries";
import { ConfirmDialog } from "@/components/admin/menu/shared";
import { cacheBootstrapFromServer } from "@/db/offlineDb";
import * as api from "@/lib/tablesApi";

/**
 * Settings → Floor → Tables & areas (owner, 2026-10-24: "the table designer
 * doesn't allow readjustment, add table, remove table, change area").
 *
 * An area is the name its tables carry; the order on every board is each table's
 * position (sort_order), areas in the order their first table appears. Every
 * change is saved at once through the tables API, which refuses by name what it
 * must (a duplicate number, deleting a table with a running order).
 */
const failure = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map(d => d?.msg || String(d)).join("; ");
  return error?.message || fallback;
};

const nameOf = table => table.label || `Table ${table.number}`;

export const groupByArea = tables => {
  const areas = [];
  for (const table of [...tables].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.number - b.number)) {
    const name = table.floor || "Main";
    let area = areas.find(a => a.name === name);
    if (!area) areas.push((area = { name, tables: [] }));
    area.tables.push(table);
  }
  return areas;
};

/** The sort_order every table should have for this arrangement: 10, 20, 30 … */
export const positions = areas => {
  const out = new Map();
  let n = 0;
  for (const area of areas) for (const table of area.tables) out.set(table.id, (n += 10));
  return out;
};

/**
 * `view`: "tables" lists every table under its area heading (name, number, seats,
 * area, order, remove); "areas" lists the areas themselves (rename, order, sort, add).
 * One component, so both pages share the same tested save path (Floor hub, 2026-10-25).
 */
export default function TableManager({ view = "all" }) {
  const { token } = usePOS();
  const [tables, setTables] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newArea, setNewArea] = useState("");
  const [deleting, setDeleting] = useState(null);
  // Bumped on every reload so each box shows the server's value again, even
  // after the server refused a change (the box kept the refused number).
  const [generation, setGeneration] = useState(0);

  const load = useCallback(async () => {
    try {
      setTables(await api.listTables(token));
      setGeneration(g => g + 1);
      setError(null);
    } catch (e) {
      setError(failure(e, "Could not load the tables"));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const areas = useMemo(() => groupByArea(tables || []), [tables]);

  // Run a change, reload, and refresh this device's boards. Each step's refusal
  // is shown by name; nothing is half-shown as saved.
  const run = async (work, done) => {
    setBusy(true);
    try {
      await work();
      if (done) toast.success(done);
    } catch (e) {
      toast.error(failure(e, "The change was not saved"));
    } finally {
      await load();
      cacheBootstrapFromServer(token).catch(() => {});
      setBusy(false);
    }
  };

  // Save the positions an arrangement implies, only where they changed.
  const arrange = nextAreas => run(async () => {
    const want = positions(nextAreas);
    for (const table of tables) {
      const pos = want.get(table.id);
      const area = nextAreas.find(a => a.tables.some(t => t.id === table.id))?.name;
      const body = {};
      if (pos !== table.sort_order) body.sort_order = pos;
      if (area && area !== (table.floor || "Main")) body.floor = area;
      if (Object.keys(body).length) await api.updateTable(table.id, body, token);
    }
  });

  const moveTable = (areaIndex, index, by) => {
    const next = areas.map(a => ({ ...a, tables: [...a.tables] }));
    const list = next[areaIndex].tables;
    const target = index + by;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    arrange(next);
  };

  const moveArea = (index, by) => {
    const next = [...areas];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    arrange(next);
  };

  const toArea = (table, areaName) => {
    const next = areas.map(a => ({ ...a, tables: a.tables.filter(t => t.id !== table.id) }));
    const target = next.find(a => a.name === areaName);
    if (!target) return;
    target.tables.push(table);
    arrange(next.filter(a => a.tables.length));
  };

  // Owner, 2026-10-25: every area had been saved in reverse (25 … 1). One tap puts
  // an area (or every area) back in natural order by table name: 1, 2, … 10, then
  // named tables such as "Pickup Counter". Area order is kept.
  const byName = (a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true, sensitivity: "base" });
  const sortArea = areaName => arrange(areas.map(a => (
    areaName == null || a.name === areaName ? { ...a, tables: [...a.tables].sort(byName) } : a
  )));

  const renameArea = (area, name) => {
    const clean = name.trim();
    if (!clean || clean === area.name) return;
    if (areas.some(a => a.name === clean)) {
      toast.error(`There is already an area called ${clean}`);
      return;
    }
    // One server step: the tables and every captain's section that names the
    // area (renaming only the tables locked those captains out of their tables).
    run(() => api.renameArea(area.name, clean, token), `Area renamed to ${clean}`);
  };

  const saveField = (table, field, value) => {
    const body = { [field]: value };
    if (field === "number" && (!Number.isInteger(value) || value < 1)) {
      toast.error("A table number must be a whole number above 0");
      return;
    }
    if (field === "capacity" && (!Number.isInteger(value) || value < 1 || value > 50)) {
      toast.error("Seats must be between 1 and 50");
      return;
    }
    if (table[field] === value) return;
    run(() => api.updateTable(table.id, body, token));
  };

  const addTable = areaName => {
    const number = Math.max(0, ...(tables || []).map(t => t.number)) + 1;
    const area = areas.find(a => a.name === areaName);
    const last = area?.tables.at(-1);
    run(() => api.createTable({
      number,
      label: `${number} ${areaName}`,
      floor: areaName,
      capacity: 4,
      sort_order: (last?.sort_order ?? (tables?.length || 0) * 10) + 5,
    }, token), `Table ${number} added to ${areaName}`);
  };

  const addArea = () => {
    const name = newArea.trim();
    if (!name) return;
    if (areas.some(a => a.name === name)) {
      toast.error(`There is already an area called ${name}`);
      return;
    }
    setNewArea("");
    addTable(name);
  };

  const remove = table => {
    setDeleting(null);
    run(() => api.deleteTable(table.id, token), `${nameOf(table)} removed`);
  };

  if (error) return <p className="text-body text-destructive" role="alert" data-testid="table-manager-error">{error}</p>;
  if (!tables) return <LoadingState message="Reading tables…" testId="table-manager-loading" />;

  const iconButton = "inline-flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:text-foreground disabled:opacity-40";

  return (
    <section className="rounded-md border bg-card" data-testid="table-manager">
      <header className="flex flex-wrap items-center justify-between gap-snug border-b px-3 py-2">
        <div>
          <h3 className="text-body font-bold">{view === "tables" ? "Tables" : view === "areas" ? "Areas" : "Tables & areas"}</h3>
          <p className="text-micro text-muted-foreground">
            {view === "tables"
              ? "Name, number, seats and area of every table. The order here is the order on every board."
              : view === "areas"
                ? "Rename, reorder and add areas. An area's place here is its place on every board."
                : "Add, rename, move and remove tables and areas. The order here is the order on every board."}
          </p>
        </div>
        {view !== "tables" && <div className="flex items-center gap-tight">
          <Input value={newArea} onChange={e => setNewArea(e.target.value)} maxLength={30} placeholder="New area name" className="h-8 w-44" data-testid="table-manager-new-area" />
          <Button size="sm" variant="outline" onClick={() => sortArea(null)} disabled={busy} data-testid="table-manager-sort-all">
            Sort all by number
          </Button>
          <Button variant="action" size="sm" onClick={addArea} disabled={busy || !newArea.trim()} data-testid="table-manager-add-area">
            <Plus className="w-3.5 h-3.5 mr-tight" /> Add area
          </Button>
        </div>}
      </header>

      <div className="divide-y">
        {areas.map((area, areaIndex) => (
          <div key={area.name} className="p-3 space-y-tight" data-testid={`table-manager-area-${area.name}`}>
            {view === "tables" ? (
              <div className="flex flex-wrap items-center gap-tight">
                <h4 className="text-body font-semibold" data-testid={`table-manager-area-${area.name}-heading`}>{area.name}</h4>
                <span className="text-meta text-muted-foreground">{area.tables.length} tables</span>
                <Button size="sm" variant="outline" onClick={() => addTable(area.name)} disabled={busy} data-testid={`table-manager-area-${area.name}-add`}>
                  <Plus className="w-3.5 h-3.5 mr-tight" /> Add table
                </Button>
              </div>
            ) : (
            <div className="flex flex-wrap items-center gap-tight">
              <Input
                defaultValue={area.name}
                key={`${area.name}-${generation}`}
                maxLength={30}
                onBlur={e => renameArea(area, e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="h-8 w-48 font-semibold"
                aria-label={`Area name for ${area.name}`}
                data-testid={`table-manager-area-${area.name}-name`}
              />
              <span className="text-meta text-muted-foreground">{area.tables.length} tables</span>
              <button type="button" className={iconButton} onClick={() => moveArea(areaIndex, -1)} disabled={busy || areaIndex === 0} aria-label={`Move ${area.name} up`} data-testid={`table-manager-area-${area.name}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" className={iconButton} onClick={() => moveArea(areaIndex, 1)} disabled={busy || areaIndex === areas.length - 1} aria-label={`Move ${area.name} down`} data-testid={`table-manager-area-${area.name}-down`}><ArrowDown className="w-3.5 h-3.5" /></button>
              <Button size="sm" variant="ghost" onClick={() => sortArea(area.name)} disabled={busy} data-testid={`table-manager-area-${area.name}-sort`}>
                Sort by number
              </Button>
              <Button size="sm" variant="outline" onClick={() => addTable(area.name)} disabled={busy} data-testid={`table-manager-area-${area.name}-add`}>
                <Plus className="w-3.5 h-3.5 mr-tight" /> Add table
              </Button>
            </div>
            )}
            {view !== "areas" && area.tables.map((table, index) => (
              <div key={table.id} className="flex flex-wrap items-center gap-tight pl-3" data-testid={`table-manager-table-${table.id}`}>
                <Input defaultValue={table.label || ""} key={`l-${table.id}-${generation}`} maxLength={50} placeholder={`Table ${table.number}`}
                  onBlur={e => saveField(table, "label", e.target.value.trim() || null)}
                  className="h-8 w-40" aria-label="Table name" data-testid={`table-manager-table-${table.id}-name`} />
                <label className="flex items-center gap-1 text-meta text-muted-foreground">No.
                  <Input type="number" min="1" defaultValue={table.number} key={`n-${table.id}-${generation}`}
                    onBlur={e => saveField(table, "number", Number(e.target.value))}
                    className="h-8 w-20" data-testid={`table-manager-table-${table.id}-number`} />
                </label>
                <label className="flex items-center gap-1 text-meta text-muted-foreground">Seats
                  <Input type="number" min="1" max="50" defaultValue={table.capacity} key={`c-${table.id}-${generation}`}
                    onBlur={e => saveField(table, "capacity", Number(e.target.value))}
                    className="h-8 w-16" data-testid={`table-manager-table-${table.id}-seats`} />
                </label>
                <select value={area.name} onChange={e => toArea(table, e.target.value)} disabled={busy}
                  className="rounded border bg-background px-2 py-1 text-body" aria-label="Area" data-testid={`table-manager-table-${table.id}-area`}>
                  {areas.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
                </select>
                <button type="button" className={iconButton} onClick={() => moveTable(areaIndex, index, -1)} disabled={busy || index === 0} aria-label="Move up" data-testid={`table-manager-table-${table.id}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" className={iconButton} onClick={() => moveTable(areaIndex, index, 1)} disabled={busy || index === area.tables.length - 1} aria-label="Move down" data-testid={`table-manager-table-${table.id}-down`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" className={`${iconButton} text-danger`} onClick={() => setDeleting(table)} disabled={busy} aria-label={`Remove ${nameOf(table)}`} data-testid={`table-manager-table-${table.id}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        ))}
        {!areas.length && (
          <p className="p-3 text-body text-muted-foreground" data-testid="table-manager-empty">No tables yet. Add an area to start.</p>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleting)}
        title={`Remove ${deleting ? nameOf(deleting) : ""}?`}
        body="It disappears from every board. Old bills keep their table. A table with a running order cannot be removed."
        confirmLabel="Remove table"
        onConfirm={() => remove(deleting)}
        onCancel={() => setDeleting(null)}
        testId="table-manager-remove-dialog"
      />
    </section>
  );
}
